/* Recently viewed products.
 *
 * Storage: localStorage key "cnd:recently-viewed", JSON array of
 *   { handle: string, addedAt: number }, newest first, deduped by
 *   handle, capped at MAX_ITEMS.
 *
 * Source: each PDP records its own handle (from the canonical URL)
 *   the moment the page is interactive. Every page that includes the
 *   section reads the list, fetches `/products/{handle}.js` for each
 *   in parallel, and renders up to N product cards.
 *
 * Consent: default-allow. If Shopify's native Customer Privacy API
 *   is present and reports analytics = false, we don't render the
 *   section (existing items in storage are kept — no PII, GDPR-clean).
 *   If the API is absent (banner not installed, ad-blocker, app
 *   uninstalled), we render as if consent was given. Storage writes
 *   happen unconditionally so a future banner flip doesn't wipe the
 *   shopper's history. */
(function () {
  if (window.CNDRecentlyViewed) return;

  var STORAGE_KEY = "cnd:recently-viewed";
  var MAX_ITEMS = 8;
  var DRAWER_MAX = 2;

  /* --- Consent gate -------------------------------------------------- */

  function analyticsAllowed() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.analyticsAllowed === "function") {
      try {
        var v = cp.analyticsAllowed();
        if (v && typeof v.then === "function") {
          return v.then(Boolean);
        }
        return Promise.resolve(Boolean(v));
      } catch (e) {
        return Promise.resolve(true);
      }
    }
    return Promise.resolve(true);
  }

  /* --- Storage ------------------------------------------------------- */

  function read() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(function (i) {
        return i && typeof i.handle === "string" && i.handle.length;
      }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {}
  }

  function add(handle) {
    if (!handle || typeof handle !== "string") return;
    try {
      var items = read().filter(function (i) {
        return i && i.handle !== handle;
      });
      items.unshift({ handle: handle, addedAt: Date.now() });
      if (items.length > MAX_ITEMS) items = items.slice(0, MAX_ITEMS);
      write(items);
    } catch (e) {}
  }

  /* --- Product fetch + render ---------------------------------------- */

  function fetchProduct(handle) {
    return fetch("/products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(function (r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    });
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c];
    });
  }

  function formatMoney(cents) {
    if (typeof cents !== "number") return "";
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents);
      }
    } catch (e) {}
    return (cents / 100).toFixed(2);
  }

  function renderCard(product, opts) {
    opts = opts || {};
    var imgUrl = product.featured_image || "";
    var img = imgUrl
      ? '<img class="rv-card__image" src="' +
        escapeHtml(imgUrl) +
        '" alt="' +
        escapeHtml(product.title) +
        '" loading="lazy" width="200" height="200">'
      : '<div class="rv-card__image rv-card__image--placeholder" aria-hidden="true"></div>';
    var price = formatMoney(product.price);
    var cls =
      "rv-card" + (opts.compact ? " rv-card--compact" : "") + (product.available ? "" : " rv-card--soldout");
    var link = product.url || "/products/" + product.handle;
    var soldoutBadge = product.available
      ? ""
      : '<span class="rv-card__badge">' + escapeHtml("Sold out") + "</span>";
    return (
      '<a class="' +
      cls +
      '" href="' +
      escapeHtml(link) +
      '">' +
      '<div class="rv-card__media">' +
      img +
      soldoutBadge +
      "</div>" +
      '<div class="rv-card__body">' +
      '<div class="rv-card__title">' +
      escapeHtml(product.title) +
      "</div>" +
      '<div class="rv-card__price">' +
      (price ? escapeHtml(price) : "") +
      "</div>" +
      "</div>" +
      "</a>"
    );
  }

  function renderInto(host, items, opts) {
    if (!host) return Promise.resolve();
    var max = opts && opts.max ? opts.max : items.length;
    var slice = items.slice(0, max);
    if (!slice.length) {
      host.hidden = true;
      return Promise.resolve();
    }
    var grid = host.querySelector("[data-recently-viewed-grid]") || host;
    return Promise.all(
      slice.map(function (entry) {
        return fetchProduct(entry.handle).catch(function () {
          return null;
        });
      }),
    ).then(function (results) {
      var seen = {};
      var valid = results.filter(function (p) {
        if (!p) return false;
        if (seen[p.handle]) return false;
        seen[p.handle] = true;
        return true;
      });
      if (!valid.length) {
        host.hidden = true;
        var pruned = items.filter(function () {
          return false;
        });
        if (pruned.length !== items.length) write(pruned);
        return;
      }
      var fetchedHandles = valid.map(function (p) {
        return p.handle;
      });
      var stillValid = items.filter(function (i) {
        return fetchedHandles.indexOf(i.handle) !== -1;
      });
      if (stillValid.length !== items.length) write(stillValid);
      host.hidden = false;
      grid.innerHTML = valid.map(function (p) {
        return renderCard(p, opts);
      }).join("");
    });
  }

  /* --- Auto-record on product pages ---------------------------------- */

  function recordCurrentProduct() {
    if (!document.body || !document.body.classList.contains("template-product")) return;
    var canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical || !canonical.href) return;
    var m = canonical.href.match(/\/products\/([a-z0-9\-_]+)/i);
    if (m && m[1]) add(m[1]);
  }

  /* --- Render ------------------------------------------------------- */

  function render() {
    var sectionHosts = document.querySelectorAll("[data-recently-viewed]");
    var drawerHosts = document.querySelectorAll("[data-recently-viewed-drawer]");
    if (!sectionHosts.length && !drawerHosts.length) return;

    var items = read();
    if (!items.length) {
      sectionHosts.forEach(function (h) {
        h.hidden = true;
      });
      drawerHosts.forEach(function (h) {
        h.hidden = true;
      });
      return;
    }

    analyticsAllowed().then(function (allowed) {
      if (!allowed) {
        sectionHosts.forEach(function (h) {
          h.hidden = true;
        });
        drawerHosts.forEach(function (h) {
          h.hidden = true;
        });
        return;
      }
      Array.prototype.forEach.call(sectionHosts, function (h) {
        var max = parseInt(h.getAttribute("data-max") || "4", 10);
        if (!max || max < 1) max = 4;
        if (max > MAX_ITEMS) max = MAX_ITEMS;
        renderInto(h, items, { max: max });
      });
      Array.prototype.forEach.call(drawerHosts, function (h) {
        renderInto(h, items, { max: DRAWER_MAX, compact: true });
      });
    });
  }

  /* --- Bootstrap ---------------------------------------------------- */

  window.CNDRecentlyViewed = { add: add, list: read, render: render };

  function init() {
    recordCurrentProduct();
    render();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();